import React, { useState } from 'react';
import { IonAlert, IonBadge, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonModal, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { mail, location, flameOutline, checkmarkCircleOutline, logOutOutline, earth, ellipsisVerticalOutline, closeCircle } from 'ionicons/icons';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import './Settings.css';
import { Api } from '../services/api';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import ToolbarComponent from '../components/ToolbarComponent';

interface LoginPageProps extends RouteComponentProps {
  api: Api
}

export const getHostName = function (url: any){
  // "https://abc.taro.solutions/api" -> "abc"; any other address -> its host.
  const match = String(url || '').match(/\/\/([^./:]+)[./:]/);
  return match ? match[1] : String(url || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
}

const Settings: React.FC<LoginPageProps> = ({api}) => {
  const history = useHistory();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [showLocationHelp, setShowLocationHelp] = useState(false);

  const navigateToAboutPage = () => {
    history.push('/about');
  };

  // Function to request location
  const checkLocationPermission = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    try {
      // Check permission state
      const permission = await navigator.permissions.query({ name: "geolocation" });

      if (permission.state === "denied") {
        setShowLocationHelp(true);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          alert("Location access granted!");
        },
        (error) => {
          if (error.code === 1) {
            // PERMISSION_DENIED
            setShowLocationHelp(true);
          } else {
            alert("Unable to retrieve your location.");
          }
        },
        { enableHighAccuracy: true }
      );
    } catch (err) {
      console.error(err);
      alert("Error checking location permission.");
    }
  };

  
  
  return (
    <IonPage>
      <IonHeader >
        <ToolbarComponent icon={faCog} title='Settings' backbutton={false} backlink=''/>
      </IonHeader>
      <IonContent fullscreen className="custom-content">
      
        <IonCard>
          <IonCardHeader>
          
            <IonCardSubtitle>User Profile</IonCardSubtitle>
            <IonCardTitle>
              {api.authentication.user?.name}
              </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList lines="full">
              <IonItem>
                <IonIcon slot="start" icon={mail} />
                <IonLabel>Email</IonLabel>
                <IonLabel slot="end">{api.authentication.user?.email}</IonLabel>
              </IonItem>
              <IonItem>
                <IonIcon slot="start" icon={flameOutline} />
                <IonLabel>Active</IonLabel>
                <IonLabel slot="end"><IonIcon slot="start" icon={checkmarkCircleOutline} size="large" color='tertiary' /></IonLabel>
              </IonItem>
              <IonItem>
                <IonIcon slot="start" icon={earth} />
                <IonLabel >Portal</IonLabel>
                <IonLabel slot="end"><IonBadge>{getHostName(api.portalAPI)}</IonBadge></IonLabel>
              </IonItem>
              <IonItem>
                <IonIcon slot="start" icon={location} />
                <IonButton slot='end' expand="block" onClick={checkLocationPermission}>
                  Allow Location
                </IonButton>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>Legend</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-accepted'></div>&nbsp;<div>Docket Accepted</div>
            </IonList>
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-denied'></div>&nbsp;<div>Docket Denied</div>
            </IonList>
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-incomplete'></div>&nbsp;<div>Docket Incomplete</div>
            </IonList>
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-unsigned'></div>&nbsp;<div>Docket Unsigned</div>
            </IonList>
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-supervisor-rejected'></div>&nbsp;<div>Docket Supervisor Rejected</div>
            </IonList>
            <IonList lines="none" className='p-2 d-flex'>
              <div className='legend-container background-today'></div>&nbsp;<div>Docket Signed</div>
            </IonList>
          </IonCardContent>
        </IonCard>
        <div className="about-button-container">
          <IonButton expand="full" color="medium" onClick={() => setIsAlertOpen(true) }><IonIcon slot="start" icon={logOutOutline} size="large" color='dark' />Logout</IonButton>
          {/* <IonButton expand="full" color="light" onClick={navigateToAboutPage}>About</IonButton> */}
        </div>
        {/* Modal shown if permission is denied */}
          <IonModal isOpen={showLocationHelp}>
            <IonHeader>
              <IonToolbar>
                <IonTitle>Enable Location Access</IonTitle>
                <IonButton slot="end" fill="clear" color="dark" onClick={() => setShowLocationHelp(false)}>
                  <IonIcon icon={closeCircle} size="large" />
                </IonButton>
              </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
              <p>
                Location access is currently blocked. To continue using this feature,
                please allow location access for this site.
              </p>

              <h3>Steps to Enable Location</h3>
              <IonList>
                <IonItem>
                  1. Click the 🔒 lock icon in your browser address bar.
                </IonItem>
                <IonItem>
                  2. Find the &nbsp; <strong>Location</strong> &nbsp; permission.
                </IonItem>
                <IonItem>
                  3. Change it to &nbsp; <strong>Allow</strong>.
                </IonItem>
                <IonItem>
                  4. Refresh this page.
                </IonItem>
              </IonList>

              <h3 className='mt-2'>Alternative way</h3>
              <IonList>
                <IonItem>
                  1. Open the app link in Chrome.
                </IonItem>
                <IonItem>
                  <div>
                    2. At the top right, select More <IonIcon icon={ellipsisVerticalOutline} /> and then Settings and then Privacy and security.
                  </div>
                </IonItem>
                <IonItem>
                  3. Select &nbsp; <strong>Site settings</strong>.
                </IonItem>
                <IonItem>
                  4. Under “Permissions,” select <strong> Location</strong>.
                </IonItem>
                <IonItem>
                  5. Select the option you want as your default setting.
                </IonItem>
                <IonItem>
                  6. Refresh this page.
                </IonItem>
              </IonList>
              <IonButton
                expand="block"
                onClick={() => window.location.reload()}
              >
                I Enabled It – Retry
              </IonButton>

              <IonButton
                expand="block"
                fill="solid"
                color="dark"
                onClick={() => setShowLocationHelp(false)}
              >
                Close
              </IonButton>
            </IonContent>
          </IonModal>
      </IonContent>
      <IonAlert
          header="Confirmation"
          subHeader="Are you sure you want to logout?"
          trigger="submit-alert"
          isOpen={isAlertOpen}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
              },
            },
            {
              text: 'Confirm',
              role: 'confirm',
              handler: (e) => {
                api.logOut();
                history.push('/timesheet');
              },
            },
          ]}
          onDidDismiss={() => setIsAlertOpen(false)}
        ></IonAlert>
    </IonPage>
  );
};

export default Settings;
